import React, { useContext, useEffect, useState } from "react";
import { SubContext } from "../context/SubTransactionContext";
import { BankContext } from "../context/BankContext";
import DynamiCFormModal from "../components/DynamicFormModal";
import { ChevronLeft, Edit2 } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import PaginationNavigation from "./paginationNavigation";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {Capacitor} from '@capacitor/core';
import { handleNativeDownload } from "../utils/downloadNative"; // Ensure this path is correct!

const AccountTransaction = () => {
    // --- CONTEXT ---
    const { getTransactions, paginatedTransaction, isTxLoading, totalPages, currentPage, gotoPage, gotoNext, goPrev, allTransaction, totalTransaction, startIndex, endIndex } = useContext(SubContext);
    const { getBank, bank, isBankLoading, addMoney, spendMoney, addBank, updateBank } = useContext(BankContext);
    const navigate = useNavigate();

    // --- STATE ---
    const [formData, setFormData] = useState({ amount: 0, description: '' });
    const [formError, setFormError] = useState({});
    const [bankDetails, setBankDetails] = useState({ accountName: '', accountNumber: '', ifscCode: '', accountType: 'Saving' });
    const [editingId, setEditingId] = useState(null);

    // --- EFFECTS ---
    useEffect(() => {
        const fetchData = async () => {
            await getTransactions();
            await getBank();
        };
        fetchData();
    }, []);

    // --- BANK MODAL HANDLERS (omitted for brevity) ---
    const openBankModal = () => {
        setEditingId(null);
        setBankDetails({ accountName: '', accountNumber: '', ifscCode: '', accountType: 'Saving' });
        document.getElementById("bank-modal").showModal();
    }
    const handleBankFormChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === "ifscCode") {
            newValue = value.toUpperCase();
        }
        setBankDetails((prevState) => ({ ...prevState, [name]: newValue }));
    }
    const handleBankSubmit = async (e) => {
        e.preventDefault();
        if (editingId) {
            await updateBank(bankDetails);
        } else {
            await addBank(bankDetails);
        }
        document.getElementById("bank-modal").close();
    }
    const handleEditBank = (bank) => {
        document.getElementById("bank-modal").showModal();
        setEditingId(bank._id);
        setBankDetails((prevState) => ({ ...prevState, ...bank }));
    }

    // --- MONEY MODAL HANDLERS (omitted for brevity) ---
    const openAddMoneyModal = () => {
        setFormData({ amount: 0, description: '' });
        setFormError({});
        document.getElementById('add-money-modal').showModal();
    };
    const openSpendMoneyModal = () => {
        setFormData({ amount: 0, description: '' });
        setFormError({});
        document.getElementById('spend-money-modal').showModal();
    };
    const handleMoneyFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleAddMoneySubmit = async (e) => {
        e.preventDefault();
        if (formData.amount <= 0) {
            setFormError({ amount: '*invalid amount' });
            return;
        }
        await addMoney(formData);
        document.getElementById('add-money-modal').close();
        await getTransactions();
    };
    const handleSpendMoneySubmit = async (e) => {
        e.preventDefault();
        if (formData.amount <= 0) {
            setFormError({ amount: '*invalid amount' });
            return;
        }
        await spendMoney(formData);
        document.getElementById('spend-money-modal').close();
        await getTransactions();
    };

    // --- PDF HANDLERS ---

    const downloadSingleTransactionPDF = (txn) => {
        // Ensure bank details are loaded
        if (!bank) return;

        const doc = new jsPDF();

        // 1. Title
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Transaction Receipt", 14, 20); 

        // 2. Bank Details (Normal font, 10pt)
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Account Name: ${bank.accountName}`, 14, 35); 
        doc.text(`Account Number: ${bank.accountNumber}`, 14, 42);  
        doc.text(`IFSC Code: ${bank.ifscCode}`, 14, 49); 
        
        // 3. Transaction Details Header
        doc.setFontSize(12);
        doc.setFont('helvetica', "bold");
        doc.text("Transaction Details", 14, 65); 
        
        // 4. Transaction Fields (Normal font, 10pt)
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        
        doc.text(
            `Date: ${new Date(txn.date).toLocaleDateString("en-IN", { 
                day: "2-digit", 
                month: "long", 
                year: "numeric" 
            })}`,
            14, 
            75
        );
        
        doc.text(`Type: ${txn.type}`, 14, 82);
        
        doc.text(`Amount: Rs ${txn.amount.toLocaleString('en-IN')}`, 14, 89);
        
        doc.text(`Description: ${txn.description || "-"}`, 14, 96); 

        // 5. Status Box (Color and Layout Match)
        
        // Set color based on transaction type
        if (txn.type === "CREDIT") {
            doc.setFillColor(34, 197, 94); // Green color
        } else {
            doc.setFillColor(239, 68, 68); // Red color
        }
        
        // Draw the filled rectangle (Location adjusted to match sample PDF)
        doc.rect(160, 72, 35, 10, "F");
        
        // Text inside the box
        doc.setTextColor(255, 255, 255); // White text color
        doc.setFont('helvetica', "bold");
        doc.setFontSize(10);
        doc.text(txn.type, 177.5, 78.5, { align: "center" }); 

        // 6. Footer (Generated On)
        doc.setTextColor(0, 0, 0); // Reset text color to black
        doc.setFontSize(8);
        doc.text(
            `Generated on: ${new Date().toLocaleDateString("en-IN", {day: "2-digit", month: "2-digit", year: "numeric"})}, ${new Date().toLocaleTimeString("en-IN", {hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false})}`, // [cite: 10]
            14,
            doc.internal.pageSize.height - 10
        );
        
        const fileName = `Receipt_${txn._id}.pdf`
        
        if(Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios'){
            handleNativeDownload(doc,fileName);
        }else{
            doc.save(fileName);
        }
    }

    const downloadTransactionsReportPDF = () => {
        const doc = new jsPDF();
        // --- PDF Generation Logic ---
        doc.setFontSize(20);
        doc.text('Admin Transaction Report', 14, 20);

        const tableHead = [["Date", "User", "Type", "Amount", "Description"]];
        const tableData = allTransaction.map(txn => [
            new Date(txn.date).toLocaleDateString('en-GB'),
            txn.userId?.name || 'N/A',
            txn.type,
            `Rs. ${txn.amount.toLocaleString('en-IN')}`,
            txn.description || "-"
        ]);

        autoTable(doc, {
            startY: 60,
            head: tableHead,
            body: tableData,
            theme: "grid",
            headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: "bold" },
        });

        const fileName = `Admin_Report_${new Date().toISOString().slice(0, 10)}.pdf`
        
        if(Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios'){
            handleNativeDownload(doc,fileName);
        }else{
            doc.save(fileName);
        }
    }

    return (
        <>
            <div className="bg-gray-50 m-2 min-h-screen p-6 relative">
                <div className="absolute top-0 left-2">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate("/library-admin")}
                        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
                    >
                        <svg
                            className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        <span className="font-medium">Back to Dashboard</span>
                    </button>
                </div>

                {/* --- CONDITIONAL RENDER START --- */}
                {isBankLoading || isTxLoading ? (
                    // --- STATE 1: LOADING ---
                    <div className="text-center text-gray-500 pt-32">
                        Loading Account Details...
                    </div>
                ) : !bank ? (
                    // --- STATE 2: NO BANK FOUND ---
                    <div className="text-center pt-32 flex flex-col items-center">
                        <h1 className="text-2xl font-semibold text-gray-700 mb-4">No Bank Account Found</h1>
                        <p className="text-gray-500 mb-8">Please add a bank account to manage transactions.</p>
                        <button
                            onClick={openBankModal}
                            className="bg-blue-600 text-white rounded-md px-6 py-3 hover:bg-blue-700 font-medium transition-colors duration-200"
                        >
                            Add Bank Account
                        </button>
                    </div>
                ) : (
                    // --- STATE 3: BANK FOUND (Main View) ---
                    <div className="flex flex-col p-2">
                        <div className="mb-8 grid grid-cols-1 md:grid-cols-5 gap-6">

                            {/* --- CARD 1: BANK DETAILS --- */}
                            <div className="md:col-span-2 bg-white shadow-lg rounded-lg p-5">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">{bank.accountName}</h3>
                                        <p className="text-sm text-gray-500 capitalize">{bank.accountType} Account</p>
                                    </div>
                                    <button onClick={() => handleEditBank(bank)} className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium">
                                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                                    </button>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-medium">A/C No:</span>
                                        <span className="font-mono text-gray-700 font-medium">{bank.accountNumber}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-medium">IFSC:</span>
                                        <span className="font-mono text-gray-700 font-medium uppercase">{bank.ifscCode}</span>
                                    </div>
                                </div>
                            </div>

                            {/* --- CARD 2: CURRENT BALANCE --- */}
                            <div className="bg-white shadow-lg rounded-lg p-5 flex flex-col justify-center text-center">
                                <p className="text-sm font-medium text-gray-500 uppercase">Available Balance</p>
                                <p className="text-4xl font-bold text-emerald-600">
                                    {bank.balance.toLocaleString('en-IN', {
                                        style: 'currency', currency: 'INR', maximumFractionDigits: 0
                                    })}
                                </p>
                            </div>

                            {/* --- CARD 3 & 4: ACTION BUTTONS --- */}
                            <div className="flex flex-col space-y-4">
                                <button
                                    onClick={openAddMoneyModal}
                                    className="w-full bg-green-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-green-700 transition-colors"
                                >
                                    Add Money
                                </button>
                                <button
                                    onClick={openSpendMoneyModal}
                                    className="w-full bg-red-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-red-700 transition-colors"
                                >
                                    Spend Money
                                </button>

                            </div>
                            <div className="md:col-span-1 flex">
                                <button
                                    onClick={downloadTransactionsReportPDF}
                                    className="w-full bg-blue-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-blue-700 transition-colors"
                                >
                                    Download Report
                                </button>
                            </div>
                        </div>

                        {/* 🛑 START OF MOBILE SCROLL FIX 🛑 */}
                        <h2 className="text-xl font-semibold mb-3 text-gray-800">Transaction History</h2>
                        
                        <div className="p-0">
                            {/* New Wrapper for Horizontal Scrolling */}
                            <div className="overflow-x-auto rounded-md shadow-lg">

                                {/* Inner div to manage the table's width (min-w-full forces it to respect table width) */}
                                <div className="inline-block min-w-full align-middle">
                                    
                                    {/* Table Scroll Area (Vertical) */}
                                    <div className="overflow-y-auto max-h-[600px] bg-white">
                                        
                                        {/* Table itself - use min-w-full to force horizontal expansion */}
                                        <table className="min-w-full divide-y divide-gray-200">
                                            
                                            <thead className="sticky top-0 bg-gray-100 text-gray-700">
                                                <tr>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left">SR.NO</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[120px]">Name</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[100px]">Date</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left">Type</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[120px]">Amount</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[150px]">Description</th>
                                                    <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[80px]">Actions</th>
                                                </tr>
                                            </thead>
                                            
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {paginatedTransaction.map((data, index) => (
                                                    <tr key={data._id} className="hover:bg-gray-50 transition-colors duration-150">
                                                        <td className="py-3 px-4 text-sm text-gray-500">{startIndex + index + 1}</td>
                                                        <td className="py-3 px-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                            {data.userId?.name || <span className="text-gray-400">N/A</span>}
                                                            {data.userId?.role === "admin" && <span className="text-[11px] text-red-500 ml-1">(Admin)</span>}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">
                                                            {new Date(data.date).toLocaleDateString('en-GB')}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                                                data.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                            }`}>
                                                                {data.type}
                                                            </span>
                                                        </td>
                                                        <td className={`py-3 px-4 text-sm font-bold whitespace-nowrap ${
                                                            data.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                            {data.amount.toLocaleString('en-IN', {
                                                                style: 'currency', currency: 'INR', maximumFractionDigits: 0
                                                            })}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-gray-500 max-w-[200px] truncate">{data.description}</td>
                                                        <td className='py-3 px-4 text-center'>
                                                            <button onClick={() => downloadSingleTransactionPDF(data)} className='px-3 py-1 text-xs text-white bg-gray-700 hover:bg-gray-800 rounded-full'>
                                                                PDF
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* 🛑 Pagination is INSIDE the overflow-x-auto container now */}
                                    {totalPages > 1 && (
                                        <div className="pb-4 pt-2 pl-2 pr-2 bg-gray-200 rounded-b-md">
                                            <div className="flex flex-wrap justify-between items-center w-full px-2">
                                                {/* Status text */}
                                                <p className="text-sm text-gray-600 flex justify-center items-center py-2">
                                                    showing {startIndex + 1} - {Math.min(endIndex, totalTransaction)} of {totalTransaction} records
                                                </p>
                                                {/* Pagination Component */}
                                                <PaginationNavigation
                                                    totalPages={totalPages}
                                                    currentPage={currentPage}
                                                    movetoPage={gotoPage}
                                                    goPrevPage={goPrev}
                                                    goNextPage={gotoNext}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* 🛑 END OF MOBILE SCROLL FIX 🛑 */}

                    </div>
                )}
                {/* --- CONDITIONAL RENDER END --- */}
            </div>

            {/* --- MODALS --- */}
            {/* 9. Bank Add/Edit Modal (Copied from BankManagement) */}
            <DynamiCFormModal id={"bank-modal"} title={editingId ? "Edit Account" : "Add Account"}>
                <form onSubmit={handleBankSubmit} className="mt-8 flex flex-col justify-center">
                    <label htmlFor="accountName" className="modal-form-lable">Account Name</label>
                    <input
                        type="text"
                        name="accountName"
                        className="modal-form-input"
                        value={bankDetails.accountName}
                        required
                        onChange={handleBankFormChange}
                    />
                    <label htmlFor="accountNumber" className="modal-form-lable">Account Number</label>
                    <input
                        type="text"
                        name="accountNumber"
                        maxLength={18}
                        className="modal-form-input"
                        value={bankDetails.accountNumber}
                        required
                        onChange={handleBankFormChange}
                    />
                    <label htmlFor="ifscCode" className="modal-form-lable">IFSC CODE</label>
                    <input
                        type="text"
                        name="ifscCode"
                        maxLength={11}
                        className="modal-form-input"
                        value={bankDetails.ifscCode}
                        required
                        onChange={handleBankFormChange}
                    />
                    <label htmlFor="accountType" className="modal-form-lable">Account Type</label>
                    <select
                        onChange={handleBankFormChange}
                        name="accountType"
                        className="modal-form-input"
                        value={bankDetails.accountType}
                        required
                    >
                        <option value="Saving">Saving</option>
                        <option value="Current">Current</option>
                    </select>
                    <div className="mt-8 border-t-2 pt-2 flex justify-end">
                        <button type="submit" className={` ${editingId ? "bg-green-500 hover:bg-green-600" : "hover:bg-black bg-gray-800"} text-gray-100 px-3 py-2 rounded-md`}>
                            {editingId ? "Save Details" : "Add Account"}
                        </button>
                    </div>
                </form>
            </DynamiCFormModal>

            {/* 10. Add Money Modal (Existing) */}
            <DynamiCFormModal id={"add-money-modal"} title={"Add Money (Credit)"}>
                <form onSubmit={handleAddMoneySubmit} className="mt-8 flex flex-col justify-center">
                    <label htmlFor="amount" className="modal-form-lable">Amount</label>
                    <input
                        type="number"
                        name="amount"
                        className="modal-form-input"
                        value={formData.amount}
                        onChange={handleMoneyFormChange}
                    />
                    {formError.amount && <span className="text-sm text-red-500">{formError.amount}</span>}
                    <label htmlFor="description" className="modal-form-lable">Description</label>
                    <input
                        type="text"
                        name="description"
                        className="modal-form-input"
                        value={formData.description}
                        onChange={handleMoneyFormChange}
                    />
                    <div className="mt-8 border-t-2 pt-2 flex justify-end">
                        <button type="submit" className="bg-green-600 hover:bg-green-700 text-gray-100 px-3 py-2 rounded-md">Add Credit</button>
                    </div>
                </form>
            </DynamiCFormModal>

            {/* 12. Spend Money Modal (Existing) */}
            <DynamiCFormModal id={"spend-money-modal"} title={"Spend Money (Debit)"}>
                <form onSubmit={handleSpendMoneySubmit} className="mt-8 flex flex-col justify-center">
                    <label htmlFor="amount" className="modal-form-lable">Amount</label>
                    <input
                        type="number"
                        name="amount"
                        className="modal-form-input"
                        value={formData.amount}
                        onChange={handleMoneyFormChange}
                    />
                    {formError.amount && <span className="text-sm text-red-500">{formError.amount}</span>}
                    <label htmlFor="description" className="modal-form-lable">Description</label>
                    <input
                        type="text"
                        name="description"
                        className="modal-form-input"
                        value={formData.description}
                        onChange={handleMoneyFormChange}
                    />
                    <div className="mt-8 border-t-2 pt-2 flex justify-end">
                        <button type="submit" className="bg-red-600 hover:bg-red-700 text-gray-100 px-3 py-2 rounded-md">Add Debit</button>
                    </div>
                </form>
            </DynamiCFormModal>
        </>
    );
};

export default AccountTransaction;